import { publicClientConfig, type PublicClientLogLevel } from '@/config/public-client-config';
import { sanitizeForLog } from '@/shared/logging/sanitize-log';

export type ClientLogMetadata = Record<string, unknown>;

export type ClientLogger = {
    trace: (message: string, metadata?: ClientLogMetadata) => void;
    debug: (message: string, metadata?: ClientLogMetadata) => void;
    info: (message: string, metadata?: ClientLogMetadata) => void;
    warn: (message: string, metadata?: ClientLogMetadata) => void;
    error: (message: string, metadata?: ClientLogMetadata) => void;
    log: (message: string, metadata?: ClientLogMetadata) => void;
    child: (bindings: ClientLogMetadata) => ClientLogger;
};

const CLIENT_LOG_SERVICE = 'marketplace-reviews';
const CLIENT_LOG_LEVEL_PRIORITY: Record<PublicClientLogLevel, number> = {
    trace: 10,
    debug: 20,
    info: 30,
    warn: 40,
    error: 50,
};
const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === '[object Object]';

const shouldEmitLogLevel = (level: PublicClientLogLevel, threshold: PublicClientLogLevel): boolean =>
    CLIENT_LOG_LEVEL_PRIORITY[level] >= CLIENT_LOG_LEVEL_PRIORITY[threshold];

const sanitizeLogMessage = (message: string): string => {
    const sanitized = sanitizeForLog(message, {
        redactSensitiveKeys: false,
    });
    return typeof sanitized === 'string' ? sanitized : String(sanitized);
};

const sanitizeLogMetadata = (metadata?: ClientLogMetadata): ClientLogMetadata | undefined => {
    if (!metadata || Object.keys(metadata).length === 0) {
        return undefined;
    }

    const sanitized = sanitizeForLog(metadata);
    return isObjectRecord(sanitized) ? sanitized : { value: sanitized };
};

const getConsoleMethod = (level: 'trace' | 'debug' | 'info' | 'warn' | 'error') => {
    switch (level) {
        case 'trace':
        case 'debug':
            return console.debug;
        case 'warn':
            return console.warn;
        case 'error':
            return console.error;
        default:
            return console.log;
    }
};

const createClientLoggerWithBindings = (scope: string, bindings: ClientLogMetadata = {}): ClientLogger => {
    const write = (
        level: 'trace' | 'debug' | 'info' | 'warn' | 'error',
        message: string,
        metadata?: ClientLogMetadata,
    ) => {
        if (!shouldEmitLogLevel(level, publicClientConfig.logLevel)) {
            return;
        }

        const consoleMethod = getConsoleMethod(level);
        const sanitizedMetadata = sanitizeLogMetadata({
            service: CLIENT_LOG_SERVICE,
            runtime: 'client',
            scope,
            ...bindings,
            ...(metadata ?? {}),
        });

        if (sanitizedMetadata) {
            consoleMethod(sanitizeLogMessage(message), sanitizedMetadata);
            return;
        }

        consoleMethod(sanitizeLogMessage(message));
    };

    return {
        trace: (message, metadata) => write('trace', message, metadata),
        debug: (message, metadata) => write('debug', message, metadata),
        info: (message, metadata) => write('info', message, metadata),
        warn: (message, metadata) => write('warn', message, metadata),
        error: (message, metadata) => write('error', message, metadata),
        log: (message, metadata) => write('info', message, metadata),
        child: (nextBindings) =>
            createClientLoggerWithBindings(scope, {
                ...bindings,
                ...nextBindings,
            }),
    };
};

export const createClientLogger = ({ scope, context }: { scope: string; context?: ClientLogMetadata }): ClientLogger =>
    createClientLoggerWithBindings(scope, context);

export const logClientError = ({
    logger,
    message,
    error,
    context,
}: {
    logger: ClientLogger;
    message: string;
    error: unknown;
    context?: ClientLogMetadata;
}) => {
    logger.error(message, {
        ...(context ?? {}),
        error,
    });
};

export const logClientWarning = ({
    logger,
    message,
    error,
    context,
}: {
    logger: ClientLogger;
    message: string;
    error: unknown;
    context?: ClientLogMetadata;
}) => {
    logger.warn(message, {
        ...(context ?? {}),
        error,
    });
};
